import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Packages.css';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

function Packages({ apiBase }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    duration_days: 30,
    description: '',
    features: []
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBase}/api/admin/package-stats`);
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      price: pkg.price,
      duration_days: pkg.duration_days,
      description: pkg.description || '',
      features: [...pkg.features]
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      price: '',
      duration_days: 30,
      description: '',
      features: []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
    setFeatureInput('');
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackage) {
        await axios.put(`${apiBase}/api/admin/packages/${editingPackage.id}`, formData);
        alert('Package updated successfully!');
      } else {
        await axios.post(`${apiBase}/api/admin/packages`, formData);
        alert('Package created successfully!');
      }
      closeModal();
      loadPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Failed to save package');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`${apiBase}/api/admin/packages/${id}`);
        alert('Package deleted successfully!');
        loadPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        alert(error.response?.data?.message || 'Failed to delete package');
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading packages...</div>;
  }

  return (
    <div className="packages-container">
      <div className="packages-header">
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FiPlus /> Create New Package
        </button>
      </div>

      <div className="packages-grid">
        {packages.map((pkg) => (
          <div key={pkg.id} className="package-card">
            <div className="package-actions">
              <button className="icon-btn" onClick={() => openEditModal(pkg)} title="Edit">
                <FiEdit2 />
              </button>
              <button className="icon-btn danger" onClick={() => handleDelete(pkg.id, pkg.name)} title="Delete">
                <FiTrash2 />
              </button>
            </div>

            <div className="package-header">
              <h3>{pkg.name}</h3>
              <div className="package-price">KES {parseFloat(pkg.price).toFixed(2)}/mo</div>
            </div>

            <div className="package-stats">
              <div className="package-stat">
                <div className="stat-value">{pkg.total_subscriptions}</div>
                <div className="stat-label">Total Subscriptions</div>
              </div>
              <div className="package-stat">
                <div className="stat-value">{pkg.active_subscriptions}</div>
                <div className="stat-label">Active</div>
              </div>
              <div className="package-stat">
                <div className="stat-value">KES {parseFloat(pkg.revenue).toLocaleString('en-KE')}</div>
                <div className="stat-label">Revenue</div>
              </div>
            </div>

            <div className="package-features">
              <h4>Features:</h4>
              <ul>
                {pkg.features.map((feature, index) => (
                  <li key={index}>✓ {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPackage ? 'Edit Package' : 'Create New Package'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Package Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Premium Package"
                />
              </div>

              <div className="form-group">
                <label>Price (KES) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="e.g., 900"
                />
              </div>

              <div className="form-group">
                <label>Duration (days)</label>
                <input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  placeholder="30"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Package description..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Features</label>
                <div className="feature-input">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <button type="button" onClick={addFeature} className="btn btn-small">Add</button>
                </div>
                <ul className="features-list">
                  {formData.features.map((feature, index) => (
                    <li key={index}>
                      {feature}
                      <button type="button" onClick={() => removeFeature(index)}>×</button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn">Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editingPackage ? 'Update' : 'Create'} Package
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Packages;
